// Mock exercise content for the /mockup/v2 student lesson players (mobile and
// desktop share it). Purely illustrative — swap for the native exercise API when
// wiring for real.
//
// Every kind below stands for a legacy DialogPrompt subclass a student meets
// inside a lesson — see getClassNameDescription() in
// component/dialogPrompt/DialogPromptForm.tsx. Each interface names the class it
// mirrors. The v2 behaviour decisions (what follows legacy, what deliberately
// changes for the card interface) are recorded in doc/StudentMockupV2.md.
//
// Settings prompts (teacher voice, audio speed, scoring level, name, avatar) live
// on the Settings/Profile pages. Start Class, Learn with Characters, Length of
// Time, the notes sidebar and the deprecated Matching on Vocabulary prompt are
// not part of v2.

import { StudentLesson } from './mockData';

export type SoundGrade = 'good' | 'ok' | 'bad';

export interface CharacterGrade {
    character: string;
    pinyinExpected: string;
    pinyinSpoken: string;
    initial: SoundGrade;
    final: SoundGrade;
    tone: SoundGrade;
    // undefined ≡ 'speech'. 'silence' = expected sound not heard; 'extra' = inserted sound.
    alignment?: 'speech' | 'silence' | 'extra';
}

const soundGradeByLetter: Record<string, SoundGrade> = { g: 'good', o: 'ok', b: 'bad' };

// Compact authoring for per-character grades: one "|"-separated entry per
// character, written as "<character> <expected pinyin> <initial><final><tone>
// [spoken pinyin]" with each grade letter g(ood) / o(k) / b(ad). The spoken
// pinyin defaults to the expected one; "-" marks a sound that was not heard.
function charGrades(spec: string): CharacterGrade[] {
    return spec.split('|').map(entry => {
        const [character, pinyinExpected, letters, pinyinSpoken] = entry.trim().split(/\s+/);
        const grade: CharacterGrade = {
            character,
            pinyinExpected,
            pinyinSpoken: pinyinSpoken ?? pinyinExpected,
            initial: soundGradeByLetter[letters[0]],
            final: soundGradeByLetter[letters[1]],
            tone: soundGradeByLetter[letters[2]],
        };
        return pinyinSpoken === '-' ? { ...grade, pinyinSpoken: '—', alignment: 'silence' } : grade;
    });
}

// ---------------------------------------------------------------------------
// Shared authoring options
// ---------------------------------------------------------------------------

// Scoring outcome of one recording: Good passes; Medium and Bad offer retry.
// Skip becomes available after two unsuccessful graded takes.
export type ScoreOutcome = 'good' | 'medium' | 'bad';

// The mockup has no real scoring, so each speaking target scripts its takes. The
// last outcome repeats. The default walks through all three outcomes once.
export const DEFAULT_TAKES: ScoreOutcome[] = ['bad', 'medium', 'good'];

// Optional support the teacher switches on per exercise in the create-exercise
// page. Everything defaults to the legacy presentation (see displayDefaults);
// the v2 additions (English, pictures, examples, starters…) default off.
export interface DisplayOptions {
    pinyin?: boolean;
    english?: boolean;
    picture?: boolean;
    example?: boolean;
    // Character-recognition and similar reading checks: let the student hear the answer.
    audio?: boolean;
    // Picture choices: label each picture with its characters.
    characters?: boolean;
    // Free speaking: response starters.
    starters?: boolean;
    info?: boolean;
    definition?: boolean;
}

// Teacher-authored text. These replace the generic messages whenever present —
// they often carry instructions or teaching material. Only the legacy fields
// that still make sense on an activity card are carried over.
export interface TeacherText {
    // Legacy `prompt` bubble: the instruction shown at the top of the card.
    intro?: string;
    // yes_response / no_response: shown after a correct / incorrect answer.
    correct?: string;
    incorrect?: string;
    // spoken_yes_response / spoken_no_response: after a passing / failing recording.
    spokenCorrect?: string;
    spokenIncorrect?: string;
    // teacher_ask_student_to_speak_prompt.
    askToSpeak?: string;
}

interface ExerciseBase {
    emoji?: string;
    infoText?: string;
    definition?: string;
    example?: Sentence;
    id: string;
    display?: DisplayOptions;
    teacher?: TeacherText;
}

export interface Sentence {
    hanzi: string;
    pinyin: string;
    english?: string;
}

// A sentence the student must say, with the grades a passing take produces.
export interface SpeakingTarget extends Sentence {
    grades: CharacterGrade[];
    takes?: ScoreOutcome[];
}

// ---------------------------------------------------------------------------
// Exercise kinds
// ---------------------------------------------------------------------------

// DialogPromptModel ("Dialog Only") / DialogPromptImageModel ("Dialog w/ Image").
// The emoji stands in for the authored image; tapping it opens the full-size view.
export interface ImageExercise extends ExerciseBase {
    kind: 'image';
    emoji?: string;
    caption: string;
    chinese?: Sentence;
}

// DialogPromptIntroduceWordModel ("Dialog w/ Character Listen"). The word audio
// must be heard before Continue. infoText is new_word_info_text.
export interface WordIntroExercise extends ExerciseBase {
    kind: 'word';
    hanzi: string;
    pinyin: string;
    meaning: string;
    emoji: string;
    infoText?: string;
    example?: Sentence;
}

// DialogPromptListenSpeakModel (graded) / DialogPromptListenSpeakNoGradeModel
// (scored as feedback only; any take completes).
export interface ListenRepeatExercise extends ExerciseBase {
    kind: 'listenRepeat';
    graded: boolean;
    target: SpeakingTarget;
    infoText?: string;
}

export interface QuizOption {
    infoText?: string;
    definition?: string;
    example?: Sentence;
    hanzi?: string;
    pinyin?: string;
    english?: string;
    // Picture answer (legacy answer image).
    emoji?: string;
    // Audio quiz: legacy "Display this word also".
    displayWord?: string;
}

// DialogPromptQuizQuestionModel ('text' / 'picture' answers),
// DialogPromptQuizQuestionAudioModel ('audio') and
// DialogPromptQuizQuestionSpeakModel (any mode plus speakAfter).
export interface QuizExercise extends ExerciseBase {
    kind: 'quiz';
    mode: 'text' | 'picture' | 'audio';
    prompt: string;
    // Question illustration (legacy prompt image).
    promptEmoji?: string;
    // Legacy teacher_chinese_prompt, now an optional line under the question.
    teacherQuestion?: Sentence;
    options: QuizOption[];
    correctIndex: number;
    // "…then Speak": legacy speaks the correct answer's own audio sentence, which
    // can be longer than the option itself.
    speakAfter?: { instruction: string; sentence: SpeakingTarget; showPinyin?: boolean };
}

// DialogPromptQuizWordModel — "Quiz: Character Recognition". A reading check;
// answer audio and pinyin are teacher options, off by default.
export interface QuizWordExercise extends ExerciseBase {
    kind: 'quizWord';
    prompt: string;
    answer: Sentence;
    distractors: Sentence[];
}

// Legacy MatchEnum: the "first word" is the vocabulary word, the "second word"
// its authored audio sentence / second expression.
export type MatchSide = 'firstCharacter' | 'firstPinyin' | 'secondCharacter' | 'secondPinyin' | 'secondAudio' | 'image' | 'english';

export interface MatchPair {
    hanzi: string;
    pinyin: string;
    english: string;
    emoji: string;
    second: Sentence;
}

// DialogPromptMatchGameModel — "Quiz: Matching Game". left_match / right_match
// pick what each column shows.
export interface MatchGameExercise extends ExerciseBase {
    kind: 'match';
    prompt: string;
    leftMatch: MatchSide;
    rightMatch: MatchSide;
    pairs: MatchPair[];
}

// DialogPromptImageSelectSpeakModel. A genuine question with no single right
// answer: every picture is valid, the pick locks, and each picture carries its
// own cue (teacher_english_prompt) and sentence to say.
export interface ImageSelectSpeakExercise extends ExerciseBase {
    kind: 'imageSelectSpeak';
    prompt: string;
    question: Sentence;
    options: {
        emoji: string;
        hanzi: string;
        pinyin: string;
        cue: string;
        sentence: SpeakingTarget;
        infoText?: string;
        definition?: string;
        example?: Sentence;
    }[];
}

// DialogPromptTypeSpeakModel (version 1) / DialogPromptTypeSpeakV2Model (version 2).
//  V1: the typed answer must match one of the accepted answers (exact, then an
//      LLM grammar check); the closest accepted answer becomes the speaking model.
//  V2: no accepted list — an LLM decides; an approved answer is spoken as typed.
// A rejected answer must be fixed and re-checked. After two rejections the
// student may skip writing; they are then given a model sentence to say.
export interface TypeSpeakExercise extends ExerciseBase {
    kind: 'typeSpeak';
    version: 1 | 2;
    question: Sentence;
    instruction: string;
    // V1: accepted answers. V2: the model offered after skipping the writing.
    accepted: SpeakingTarget[];
    // Mock stand-in for the LLM check: V2 approves answers containing every token.
    mustInclude?: string[];
    // Authored reason_for_incorrect.
    reasonForIncorrect: string;
}

// DialogPromptSpeakWithGradeModel (graded) / DialogPromptSpeakNoGradeModel.
// The teacher asks a question whose audio must be heard; the student answers in
// their own words. Graded: one recording is compared with every accepted answer
// and the closest one is scored.
export interface SpeakingExercise extends ExerciseBase {
    kind: 'speak';
    graded: boolean;
    heroHanzi: string;
    heroPinyin: string;
    speaker: { nameHanzi: string; nameEnglish: string };
    question: Sentence;
    // student_response_prompt; "___" marks the legacy <Answer/> slot.
    studentCue?: string;
    // Alternatives — the student says one of them, never all of them.
    accepted: SpeakingTarget[];
    starters: string[];
    hint?: string;
}

// Three open-answer formats in the student pilot. They share one question
// surface but require different response evidence.
export interface OpenResponseExercise extends ExerciseBase {
    kind: 'openTextAudio' | 'openAudio' | 'openText';
    title: string;
    question: string;
    guidance: string;
    criteria: { text: string; role: 'required' | 'suggested' | 'example' }[];
    speakingHint?: string;
    revisionFeedback?: [string, ...string[]];
    // Lesson 403: structured feedback in place of the scripted paragraph.
    feedbackPreview?: OpenResponseFeedbackPreview;
}

// Detailed open-response feedback (lesson 403 preview): the answer sentence by
// sentence with numbered language items, and for a recording the scorer's
// per-character initial / final / tone grades, recurring sound patterns and a few
// pronunciation tips. Scripted content about a sample answer, never the
// student's own answer.
export interface FeedbackLanguageItem {
    tier: 'correction' | 'suggestion';
    span: string; // exact quote from the sample answer
    // Which match of span is meant (0-based, counting through the answer) when the
    // wording repeats; the live API would return character offsets instead.
    occurrence?: number;
    revised: string; // the whole sentence containing the span, with only this change made
    explanation: string;
    exampleZh?: string;
    exampleEn?: string;
}

export interface FeedbackSentence {
    text: string;
    clip?: string; // start of this sentence in the recording, m:ss
    grades?: CharacterGrade[];
    // Where the recording and the text shown differ: a word the pinyin recognizer
    // heard differently from the transcript, or spoken wording that left the script.
    heardDifferently?: string;
    // The recognizers disagree on this sentence, so it is shown without sound grades.
    unclear?: boolean;
}


export interface PronunciationPattern {
    label: string;
    count: number;
    examples: string[];
}

export interface PronunciationTip {
    word: string;
    expected: string;
    heard: string;
    // likely: the scorer graded a sound bad; possible: only the heard pinyin differs.
    strength: 'likely' | 'possible';
    tip: string;
    clip?: string;
}

export interface OpenResponseFeedbackPreview {
    // writing: typed answer only; script: typed answer read aloud; transcript: speech only.
    source: 'writing' | 'script' | 'transcript';
    focus: string[];
    sentences: FeedbackSentence[];
    language: FeedbackLanguageItem[];
    patterns?: PronunciationPattern[];
    tips?: PronunciationTip[];
}

export interface ConversationLine {
    speakerHanzi: string;
    speakerEnglish: string;
    hanzi: string;
    pinyin: string;
    english: string;
    side: 'left' | 'right';
    // sentence_text_alternate for 'readBlankSpeak', with the gaps already filled in.
    blankHanzi?: string;
    grades: CharacterGrade[];
    // Scripted takes for this line (default: passes first time).
    takes?: ScoreOutcome[];
}

// One card per conversation prompt class:
//   listen             → DialogPromptConversationListenModel
//   listenNoCharacters → DialogPromptConversationListenWithoutCharacterModel
//   listenSpeak        → DialogPromptConversationListenSpeakModel
//   readSpeak          → DialogPromptConversationReadSpeakModel
//   readBlankSpeak     → DialogPromptConversationReadWithBlankSpeakModel
//   speakOnly          → DialogPromptConversationSpeakOnlyModel
// Listening modes: every line must be heard, in order. Speaking modes: the
// student says every line on both sides, passing or choosing Move on before the next.
export type ConversationMode = 'listen' | 'listenNoCharacters' | 'listenSpeak' | 'readSpeak' | 'readBlankSpeak' | 'speakOnly';

export interface ConversationExercise extends ExerciseBase {
    kind: 'conversation';
    mode: ConversationMode;
    title: string;
    lines: ConversationLine[];
}

// DialogPromptSummaryModel — "Speaking Summary". `intro` may use {minutes} and
// {count} (legacy <Minutes/> and <SentenceCount/>).
export interface SummaryExercise extends ExerciseBase {
    kind: 'summary';
    title: string;
    intro: string;
    minutes: number;
    sentences: { english: string; attempts: CharacterGrade[][] }[];
}

// DialogPromptSelectNextDialogModel — a branch point, usually so the student can
// skip ahead. `response` is the authored response_text. Continue follows the
// explicit course, lesson and exercise destination.
export interface ExerciseDestination {
    courseId: number;
    lessonId: number;
    /** One-based step in the destination lesson. */
    step: number;
    /** Replay starts a fresh attempt without erasing saved progress. */
    restart?: boolean;
}

export interface ChooseNextExercise extends ExerciseBase {
    kind: 'chooseNext';
    prompt: string;
    options: { emoji: string; title: string; detail: string; response: string; destination: ExerciseDestination }[];
}

export type LessonExercise =
    | ImageExercise
    | WordIntroExercise
    | ListenRepeatExercise
    | QuizExercise
    | QuizWordExercise
    | MatchGameExercise
    | ImageSelectSpeakExercise
    | TypeSpeakExercise
    | SpeakingExercise
    | OpenResponseExercise
    | ConversationExercise
    | SummaryExercise
    | ChooseNextExercise;

// Pinyin is off for every new activity unless this mock lesson explicitly
// demonstrates the teacher's per-activity display option.
const displayDefaults: Partial<Record<LessonExercise['kind'], DisplayOptions>> = {};

export function shows(exercise: LessonExercise, option: keyof DisplayOptions): boolean {
    return exercise.display?.[option] ?? displayDefaults[exercise.kind]?.[option] ?? (option === 'info');
}

// ---------------------------------------------------------------------------
// Authored content: lesson 201 "Ordering at a Restaurant" — mirrors the lesson
// the teacher builds in the /teacher mockup (teacher/mockLesson.ts), so both
// mockups demo the same lesson from each side.
// ---------------------------------------------------------------------------

const restaurantConversationLines: ConversationLine[] = [
    {
        speakerHanzi: '服务员', speakerEnglish: 'Waiter', side: 'left',
        hanzi: '您好！欢迎光临。您想吃什么？', pinyin: 'Nín hǎo! Huānyíng guānglín. Nín xiǎng chī shénme?',
        english: 'Hello! Welcome. What would you like to eat?',
        blankHanzi: '您好！欢迎光临。您想__什么？',
        grades: charGrades('您 nín ggg | 好 hǎo ggg | 欢 huān ggg | 迎 yíng ggo | 光 guāng ggg | 临 lín ggg | 您 nín ggg | 想 xiǎng ggg | 吃 chī ggg | 什 shén ggg | 么 me ggg'),
        takes: ['medium', 'good'],
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '请给我一盘饺子和一碗米饭。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi hé yì wǎn mǐfàn.',
        english: 'Please give me a plate of dumplings and a bowl of rice.',
        blankHanzi: '请给我一盘__和一碗__。',
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggo yí | 盘 pán ggg | 饺 jiǎo ggg | 子 zi gog | 和 hé ggo | 一 yì ggg | 碗 wǎn ggb wán | 米 mǐ ggg | 饭 fàn ggg'),
        takes: ['bad', 'good'],
    },
    {
        speakerHanzi: '服务员', speakerEnglish: 'Waiter', side: 'left',
        hanzi: '好的。您想喝什么？', pinyin: 'Hǎo de. Nín xiǎng hē shénme?',
        english: 'Sure. What would you like to drink?',
        blankHanzi: '好的。您想__什么？',
        grades: charGrades('好 hǎo ggg | 的 de ggg | 您 nín ggg | 想 xiǎng ggg | 喝 hē ggo | 什 shén ggg | 么 me ggg'),
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '请给我一杯茶，谢谢！', pinyin: 'Qǐng gěi wǒ yì bēi chá, xièxie!',
        english: 'Please give me a cup of tea, thank you!',
        blankHanzi: '请给我一杯__，谢谢！',
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā | 谢 xiè ggo | 谢 xie ggg'),
    },
];

const orderingAtRestaurantExercises: LessonExercise[] = [
    {
        id: '201-image-restaurant',
        kind: 'image',
        emoji: '🍽️',
        caption: 'Today we are eating out. Look at the restaurant before we order.',
        chinese: { hanzi: '我们去饭馆吧！', pinyin: 'Wǒmen qù fànguǎn ba!' },
    },
    {
        id: '201-word-caidan',
        kind: 'word',
        hanzi: '菜单',
        pinyin: 'càidān',
        meaning: 'menu',
        emoji: '📋',
        infoText: '菜 means a dish and 单 means a list — a menu is literally a list of dishes.',
        example: { hanzi: '请给我菜单。', pinyin: 'Qǐng gěi wǒ càidān.', english: 'Please give me the menu.' },
        // The teacher turned on the v2 extras for this word to show what they look like.
        display: { english: true, picture: true, example: true, info: true },
    },
    {
        id: '201-word-fuwuyuan',
        kind: 'word',
        hanzi: '服务员',
        pinyin: 'fúwùyuán',
        meaning: 'server',
        emoji: '🤵',
        infoText: 'Call 服务员 to get a server’s attention in a restaurant.',
        example: { hanzi: '服务员，请给我一杯茶。', pinyin: 'Fúwùyuán, qǐng gěi wǒ yì bēi chá.', english: 'Server, please give me a cup of tea.' },
    },
    {
        id: '201-listen-caidan',
        kind: 'listenRepeat',
        graded: true,
        target: {
            hanzi: '请给我菜单。',
            pinyin: 'Qǐng gěi wǒ càidān.',
            english: 'Please give me the menu.',
            grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 菜 cài ggo | 单 dān ggb dǎn'),
        },
        infoText: '请 makes a request polite. Keep 单 high and level (first tone).',
        teacher: {
            intro: 'Listen to the model, then say it back with the same tones.',
            spokenCorrect: '很好！That sounds natural.',
            spokenIncorrect: 'Listen again — keep 单 high and flat, then try once more.',
        },
    },
    {
        id: '201-word-jiaozi',
        kind: 'word',
        hanzi: '饺子',
        pinyin: 'jiǎozi',
        meaning: 'dumplings',
        emoji: '🥟',
        example: { hanzi: '我要一盘饺子。', pinyin: 'Wǒ yào yì pán jiǎozi.', english: 'I want a plate of dumplings.' },
        display: { picture: true },
    },
    {
        id: '201-quiz-jiaozi-pic',
        kind: 'quiz',
        mode: 'text',
        prompt: 'Which word matches the picture?',
        promptEmoji: '🥟',
        teacherQuestion: { hanzi: '这是什么？', pinyin: 'Zhè shì shénme?' },
        options: [
            { hanzi: '米饭', pinyin: 'mǐfàn' },
            { hanzi: '饺子', pinyin: 'jiǎozi' },
            { hanzi: '菜单', pinyin: 'càidān' },
            { hanzi: '服务员', pinyin: 'fúwùyuán' },
        ],
        correctIndex: 1,
        teacher: { correct: '对了！这是饺子。', incorrect: 'Not quite — look at the picture again.' },
    },
    {
        id: '201-word-mifan',
        kind: 'word',
        hanzi: '米饭',
        pinyin: 'mǐfàn',
        meaning: 'cooked rice',
        emoji: '🍚',
        example: { hanzi: '请给我一碗米饭。', pinyin: 'Qǐng gěi wǒ yì wǎn mǐfàn.', english: 'Please give me a bowl of rice.' },
    },
    {
        id: '201-quizword-mifan',
        kind: 'quizWord',
        prompt: 'Which one is mǐfàn — cooked rice?',
        answer: { hanzi: '米饭', pinyin: 'mǐfàn' },
        distractors: [
            { hanzi: '面条', pinyin: 'miàntiáo' },
            { hanzi: '米线', pinyin: 'mǐxiàn' },
            { hanzi: '饭馆', pinyin: 'fànguǎn' },
        ],
        teacher: { incorrect: 'Look closely — both characters matter.' },
    },
    {
        id: '201-quiz-mifan-pic',
        kind: 'quiz',
        mode: 'picture',
        prompt: 'Which picture shows 一碗米饭?',
        options: [
            { emoji: '🥟', english: 'a plate of dumplings' },
            { emoji: '🍵', english: 'a cup of tea' },
            { emoji: '🍚', english: 'a bowl of rice' },
            { emoji: '🍜', english: 'a bowl of noodles' },
        ],
        correctIndex: 2,
    },
    {
        id: '201-match-dishes',
        kind: 'match',
        prompt: 'Match each word with its picture.',
        leftMatch: 'firstCharacter',
        rightMatch: 'image',
        pairs: [
            { hanzi: '菜单', pinyin: 'càidān', english: 'menu', emoji: '📋', second: { hanzi: '请给我菜单。', pinyin: 'Qǐng gěi wǒ càidān.' } },
            { hanzi: '饺子', pinyin: 'jiǎozi', english: 'dumplings', emoji: '🥟', second: { hanzi: '一盘饺子', pinyin: 'yì pán jiǎozi' } },
            { hanzi: '米饭', pinyin: 'mǐfàn', english: 'cooked rice', emoji: '🍚', second: { hanzi: '一碗米饭', pinyin: 'yì wǎn mǐfàn' } },
            { hanzi: '茶', pinyin: 'chá', english: 'tea', emoji: '🍵', second: { hanzi: '一杯茶', pinyin: 'yì bēi chá' } },
        ],
    },
    {
        id: '201-quiz-audio-jiaozi',
        kind: 'quiz',
        mode: 'audio',
        prompt: 'Play each order. Which one matches the picture?',
        promptEmoji: '🥟',
        options: [
            { hanzi: '请给我一碗米饭。', pinyin: 'Qǐng gěi wǒ yì wǎn mǐfàn.' },
            { hanzi: '请给我一杯茶。', pinyin: 'Qǐng gěi wǒ yì bēi chá.' },
            { hanzi: '请给我一盘饺子。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi.' },
            { hanzi: '请给我菜单。', pinyin: 'Qǐng gěi wǒ càidān.' },
        ],
        correctIndex: 2,
    },
    {
        id: '201-quiz-beicha',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'a cup of tea'?",
        options: [
            { hanzi: '一碗茶', pinyin: 'yì wǎn chá' },
            { hanzi: '一盘茶', pinyin: 'yì pán chá' },
            { hanzi: '一杯茶', pinyin: 'yì bēi chá' },
            { hanzi: '一杯饺子', pinyin: 'yì bēi jiǎozi' },
        ],
        correctIndex: 2,
        // This teacher shows pinyin on the options.
        display: { pinyin: true },
        teacher: { incorrect: 'Tea comes in a cup — which measure word goes with 杯?' },
    },
    {
        id: '201-quiz-speak-order',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which word means 'dumplings'?",
        options: [
            { hanzi: '米饭', pinyin: 'mǐfàn' },
            { hanzi: '饺子', pinyin: 'jiǎozi' },
            { hanzi: '面条', pinyin: 'miàntiáo' },
        ],
        correctIndex: 1,
        speakAfter: {
            instruction: '现在请你点饺子。Now order them out loud.',
            sentence: {
                hanzi: '请给我一盘饺子。',
                pinyin: 'Qǐng gěi wǒ yì pán jiǎozi.',
                english: 'Please give me a plate of dumplings.',
                grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggo yí | 盘 pán ggb pān | 饺 jiǎo ggo | 子 zi gog'),
            },
        },
        teacher: { correct: '对了！饺子 are dumplings.', askToSpeak: '现在请你点饺子。Now order them out loud.' },
    },
    {
        id: '201-imageselect-order',
        kind: 'imageSelectSpeak',
        prompt: 'Pick anything you like, then order it.',
        question: { hanzi: '您想吃什么？', pinyin: 'Nín xiǎng chī shénme?', english: 'What would you like to eat?' },
        options: [
            {
                emoji: '🥟', hanzi: '饺子', pinyin: 'jiǎozi', cue: 'Order a plate of dumplings.',
                sentence: { hanzi: '请给我一盘饺子。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi.', english: 'Please give me a plate of dumplings.', grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 盘 pán ggo | 饺 jiǎo ggo | 子 zi gog') },
            },
            {
                emoji: '🍚', hanzi: '米饭', pinyin: 'mǐfàn', cue: 'Ask for a bowl of rice.',
                sentence: { hanzi: '我要一碗米饭。', pinyin: 'Wǒ yào yì wǎn mǐfàn.', english: 'I want a bowl of rice.', grades: charGrades('我 wǒ ggg | 要 yào ggg | 一 yì ggo yí | 碗 wǎn ggb wán | 米 mǐ ggg | 饭 fàn ggg') },
            },
            {
                emoji: '🍜', hanzi: '面条', pinyin: 'miàntiáo', cue: 'Ask whether they have noodles.',
                sentence: { hanzi: '你们有面条吗？', pinyin: 'Nǐmen yǒu miàntiáo ma?', english: 'Do you have noodles?', grades: charGrades('你 nǐ ggg | 们 men ggg | 有 yǒu ggo | 面 miàn ggg | 条 tiáo gob tiāo | 吗 ma ggg') },
                infoText: 'A question with 吗 rises at the end.',
            },
            {
                emoji: '🍵', hanzi: '茶', pinyin: 'chá', cue: 'Order a cup of tea, politely.',
                sentence: { hanzi: '请给我一杯茶，谢谢。', pinyin: 'Qǐng gěi wǒ yì bēi chá, xièxie.', english: 'A cup of tea, please. Thank you.', grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā | 谢 xiè ggo | 谢 xie ggg') },
            },
        ],
    },
    {
        id: '201-convo-restaurant',
        kind: 'conversation',
        mode: 'listen',
        title: 'Ordering at a restaurant',
        lines: restaurantConversationLines,
    },
    {
        id: '201-convo-restaurant-blank',
        kind: 'conversation',
        mode: 'readBlankSpeak',
        title: 'Fill the gaps out loud',
        lines: restaurantConversationLines,
    },
    {
        id: '201-typespeak-drink',
        kind: 'typeSpeak',
        version: 1,
        question: { hanzi: '您想喝什么？', pinyin: 'Nín xiǎng hē shénme?', english: 'What would you like to drink?' },
        instruction: 'Type your answer in Chinese, then say it out loud.',
        accepted: [
            { hanzi: '请给我一杯茶。', pinyin: 'Qǐng gěi wǒ yì bēi chá.', grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā') },
            { hanzi: '我要一杯茶。', pinyin: 'Wǒ yào yì bēi chá.', grades: charGrades('我 wǒ ggg | 要 yào ggg | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā') },
            { hanzi: '请给我一杯水。', pinyin: 'Qǐng gěi wǒ yì bēi shuǐ.', grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 杯 bēi gog bei | 水 shuǐ ggo') },
        ],
        reasonForIncorrect: 'Almost — drinks take the measure word 杯 (a cup), for example 一杯茶.',
        teacher: { correct: '对了！That works — now say it out loud.', askToSpeak: 'Say your order aloud. Keep the measure word with the drink.' },
        definition: '杯 is the measure word for a cup or glass of a drink.',
        display: { definition: true },
    },
    {
        id: '201-speak-order',
        kind: 'speak',
        graded: true,
        heroHanzi: '你准备好点菜了吗？',
        heroPinyin: 'Nǐ zhǔnbèi hǎo diǎn cài le ma?',
        speaker: { nameHanzi: '服务员', nameEnglish: 'Waiter' },
        question: { hanzi: '您好！欢迎光临。您想吃什么？', pinyin: 'Nín hǎo! Huānyíng guānglín. Nín xiǎng chī shénme?', english: 'Hello! Welcome. What would you like to eat?' },
        studentCue: '请给我 ___ 。',
        accepted: [
            { hanzi: '请给我一盘饺子和一杯茶。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi hé yì bēi chá.', grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggo yí | 盘 pán ggb pān | 饺 jiǎo ggo | 子 zi gog | 和 hé ggo | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā') },
            { hanzi: '我要一碗米饭。', pinyin: 'Wǒ yào yì wǎn mǐfàn.', grades: charGrades('我 wǒ ggg | 要 yào ggg | 一 yì ggg | 碗 wǎn ggo | 米 mǐ ggg | 饭 fàn ggg') },
            { hanzi: '请给我一碗面条。', pinyin: 'Qǐng gěi wǒ yì wǎn miàntiáo.', grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 碗 wǎn ggo | 面 miàn ggg | 条 tiáo ggg') },
        ],
        display: { starters: true },
        starters: ['请给我一盘……', '我要一碗……', '你们有什么……？'],
        hint: 'Remember the measure word: 一碗 for rice, 一盘 for dumplings, 一杯 for tea.',
        teacher: { spokenCorrect: '好的，马上来！Coming right up.', spokenIncorrect: 'The waiter didn’t catch that. Order one dish with its measure word.' },
    },
    {
        id: '201-typespeak-v2-food',
        kind: 'typeSpeak',
        version: 2,
        question: { hanzi: '你喜欢吃什么？', pinyin: 'Nǐ xǐhuan chī shénme?', english: 'What do you like to eat?' },
        instruction: 'Write your own answer with 喜欢, then say it out loud.',
        accepted: [
            { hanzi: '我喜欢吃饺子。', pinyin: 'Wǒ xǐhuan chī jiǎozi.', grades: charGrades('我 wǒ ggg | 喜 xǐ ggg | 欢 huan ggo | 吃 chī ggg | 饺 jiǎo ggo | 子 zi ggg') },
        ],
        mustInclude: ['喜欢'],
        reasonForIncorrect: 'Answer the question with 喜欢, for example 我喜欢吃米饭。',
        teacher: { correct: '很好！That is a natural answer — now say your sentence.' },
    },
];

// ---------------------------------------------------------------------------
// Authored content: lesson 202 "Weekend Plans" (occupies the LAST positions of
// the lesson so the resume point lands right at the start of this arc).
// ---------------------------------------------------------------------------

const weekendConversationLines: ConversationLine[] = [
    {
        speakerHanzi: '小美', speakerEnglish: 'Xiaomei', side: 'left',
        hanzi: '这个周末你想做什么？', pinyin: 'Zhège zhōumò nǐ xiǎng zuò shénme?',
        english: 'What do you want to do this weekend?',
        blankHanzi: '这个__你想做什么？',
        grades: charGrades('这 zhè ggg | 个 ge ggg | 周 zhōu ggg | 末 mò ggo | 你 nǐ ggg | 想 xiǎng ggg | 做 zuò ggg | 什 shén ggg | 么 me ggg'),
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '我想去看电影。你呢？', pinyin: 'Wǒ xiǎng qù kàn diànyǐng. Nǐ ne?',
        english: 'I want to go see a movie. How about you?',
        blankHanzi: '我想去__电影。你呢？',
        grades: charGrades('我 wǒ ggg | 想 xiǎng ggb xiáng | 去 qù gog | 看 kàn ggg | 电 diàn ggg | 影 yǐng gbo yǐn | 你 nǐ ggg | 呢 ne ggg'),
        takes: ['medium', 'good'],
    },
    {
        speakerHanzi: '小美', speakerEnglish: 'Xiaomei', side: 'left',
        hanzi: '我打算去公园跑步。', pinyin: 'Wǒ dǎsuàn qù gōngyuán pǎobù.',
        english: 'I plan to go running in the park.',
        takes: ['bad', 'bad', 'good'],
        blankHanzi: '我打算去__跑步。',
        grades: charGrades('我 wǒ ggg | 打 dǎ ggg | 算 suàn ggo | 去 qù ggg | 公 gōng ggg | 园 yuán ggg | 跑 pǎo ggg | 步 bù ggg'),
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '好，那星期天我们一起吃晚饭吧！', pinyin: 'Hǎo, nà xīngqītiān wǒmen yìqǐ chī wǎnfàn ba!',
        english: "OK, then let's have dinner together on Sunday!",
        blankHanzi: '好，那星期天我们__吃晚饭吧！',
        grades: charGrades('好 hǎo ggg | 那 nà ggg | 星 xīng ggg | 期 qī ggo | 天 tiān ggg | 我 wǒ ggg | 们 men ggg | 一 yì ggo yí | 起 qǐ ggg | 吃 chī gob chi | 晚 wǎn ggg | 饭 fàn ggg | 吧 ba ggg'),
    },
];

const weekendPlansExercises: LessonExercise[] = [
    {
        id: '202-word-zhoumo',
        kind: 'word',
        hanzi: '周末',
        pinyin: 'zhōumò',
        meaning: 'weekend',
        emoji: '🗓️',
        infoText: '周 is a week and 末 is the end — the end of the week.',
        example: { hanzi: '周末你有空吗？', pinyin: 'Zhōumò nǐ yǒu kòng ma?', english: 'Are you free on the weekend?' },
        display: { example: true, english: true },
    },
    {
        id: '202-word-dianying',
        kind: 'word',
        hanzi: '电影',
        pinyin: 'diànyǐng',
        meaning: 'movie',
        emoji: '🎬',
        example: { hanzi: '我们去看电影吧。', pinyin: 'Wǒmen qù kàn diànyǐng ba.', english: "Let's go see a movie." },
    },
    {
        id: '202-listen-dianying',
        kind: 'listenRepeat',
        graded: false,
        target: {
            hanzi: '我们去看电影吧。',
            pinyin: 'Wǒmen qù kàn diànyǐng ba.',
            english: "Let's go see a movie.",
            grades: charGrades('我 wǒ ggg | 们 men ggg | 去 qù ggo | 看 kàn ggg | 电 diàn ggb diān | 影 yǐng ggg | 吧 ba ggg'),
            takes: ['bad', 'good'],
        },
        teacher: { intro: 'Say it back. Your score is just feedback — you can continue whatever it is.', spokenCorrect: '说得好！' },
    },
    {
        id: '202-quiz-movie',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'to watch a movie'?",
        options: [
            { hanzi: '打篮球', pinyin: 'dǎ lánqiú' },
            { hanzi: '看电影', pinyin: 'kàn diànyǐng' },
            { hanzi: '听音乐', pinyin: 'tīng yīnyuè' },
            { hanzi: '去公园', pinyin: 'qù gōngyuán' },
        ],
        correctIndex: 1,
    },
    {
        id: '202-convo-weekend-audio',
        kind: 'conversation',
        mode: 'listenNoCharacters',
        title: 'Listen first — no text',
        lines: weekendConversationLines,
    },
    {
        id: '202-convo-weekend',
        kind: 'conversation',
        mode: 'listen',
        title: 'What are you doing this weekend?',
        lines: weekendConversationLines,
        // This teacher shows the English translation under each line.
        display: { english: true },
    },
    {
        id: '202-convo-weekend-listenspeak',
        kind: 'conversation',
        mode: 'listenSpeak',
        title: 'Listen, then say every line',
        lines: weekendConversationLines,
    },
    {
        id: '202-speak-weekend',
        kind: 'speak',
        graded: true,
        heroHanzi: '你周末想做什么？',
        heroPinyin: 'Nǐ zhōumò xiǎng zuò shénme?',
        speaker: { nameHanzi: '小美', nameEnglish: 'Xiaomei' },
        question: { hanzi: '这个周末你想做什么？', pinyin: 'Zhège zhōumò nǐ xiǎng zuò shénme?', english: 'What do you want to do this weekend?' },
        studentCue: '我想去 ___ 。',
        accepted: [
            { hanzi: '我想去看电影。', pinyin: 'Wǒ xiǎng qù kàn diànyǐng.', grades: charGrades('我 wǒ ggg | 想 xiǎng ggb xiáng | 去 qù gog | 看 kàn ggb kǎn | 电 diàn ggg | 影 yǐng gbo yǐn') },
            { hanzi: '我想去公园。', pinyin: 'Wǒ xiǎng qù gōngyuán.', grades: charGrades('我 wǒ ggg | 想 xiǎng ggg | 去 qù ggg | 公 gōng ggg | 园 yuán ggo') },
        ],
        starters: ['我想去……', '我们一起……吧', '我打算……'],
        hint: '想 + a place or activity: 我想去看电影。',
        display: { english: true, starters: true },
    },
    {
        id: '202-word-gongyuan',
        kind: 'word',
        hanzi: '公园',
        pinyin: 'gōngyuán',
        meaning: 'park',
        emoji: '🌳',
        example: { hanzi: '公园里人很多。', pinyin: 'Gōngyuán lǐ rén hěn duō.', english: 'There are a lot of people in the park.' },
    },
    {
        id: '202-quiz-together',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'together'?",
        options: [
            { hanzi: '已经', pinyin: 'yǐjīng' },
            { hanzi: '以前', pinyin: 'yǐqián' },
            { hanzi: '一起', pinyin: 'yìqǐ' },
            { hanzi: '一定', pinyin: 'yídìng' },
        ],
        correctIndex: 2,
    },
    {
        id: '202-word-dasuan',
        kind: 'word',
        hanzi: '打算',
        pinyin: 'dǎsuàn',
        meaning: 'to plan / to intend',
        emoji: '📝',
        example: { hanzi: '你打算做什么？', pinyin: 'Nǐ dǎsuàn zuò shénme?', english: 'What do you plan to do?' },
    },
    {
        id: '202-convo-weekend-read',
        kind: 'conversation',
        mode: 'readSpeak',
        title: 'Read every line aloud',
        lines: weekendConversationLines,
        display: { pinyin: true },
    },
    {
        id: '202-convo-weekend-memory',
        kind: 'conversation',
        mode: 'speakOnly',
        title: 'Now from memory',
        lines: weekendConversationLines,
    },
    {
        id: '202-speak-plan',
        kind: 'speak',
        graded: true,
        heroHanzi: '说说你的周末计划',
        heroPinyin: 'Shuōshuo nǐ de zhōumò jìhuà',
        speaker: { nameHanzi: '大伟', nameEnglish: 'Dawei' },
        question: { hanzi: '这个周末你打算做什么？', pinyin: 'Zhège zhōumò nǐ dǎsuàn zuò shénme?', english: 'What do you plan to do this weekend?' },
        accepted: [
            { hanzi: '我打算去公园。', pinyin: 'Wǒ dǎsuàn qù gōngyuán.', grades: charGrades('我 wǒ ggg | 打 dǎ ggg | 算 suàn ggo | 去 qù ggg | 公 gōng ggg | 园 yuán gob yuǎn') },
            { hanzi: '我打算看电影。', pinyin: 'Wǒ dǎsuàn kàn diànyǐng.', grades: charGrades('我 wǒ ggg | 打 dǎ ggg | 算 suàn ggg | 看 kàn ggg | 电 diàn ggg | 影 yǐng ggo') },
        ],
        display: { starters: true },
        starters: ['我打算……', '星期六我要……', '我想和朋友……'],
    },
    {
        id: '202-speak-free',
        kind: 'speak',
        graded: false,
        heroHanzi: '随便说说吧！',
        heroPinyin: 'Suíbiàn shuōshuo ba!',
        speaker: { nameHanzi: '大伟', nameEnglish: 'Dawei' },
        question: { hanzi: '你喜欢周末做什么？', pinyin: 'Nǐ xǐhuan zhōumò zuò shénme?', english: 'What do you like doing on weekends?' },
        studentCue: '我喜欢 ___ 。',
        accepted: [],
        display: { starters: true },
        starters: ['我喜欢……', '我常常……'],
        hint: 'Say anything you like — this one is not scored.',
        // TODO(implementation): reply to what the student actually said instead of one authored response.
        teacher: { spokenCorrect: '听起来很有意思！That sounds fun.' },
    },
    {
        id: '202-summary',
        kind: 'summary',
        title: 'Your speaking this lesson',
        intro: 'You practiced for {minutes} and said {count} sentences. Here is how they sounded.',
        minutes: 18,
        sentences: [
            {
                english: 'I want to go see a movie.',
                attempts: [
                    charGrades('我 wǒ ggg | 想 xiǎng ggg | 去 qù ggg | 看 kàn ggg | 电 diàn ggg | 影 yǐng ggo'),
                    charGrades('我 wǒ ggg | 想 xiǎng ggb xiáng | 去 qù gog | 看 kàn ggb kǎn | 电 diàn ggg | 影 yǐng gbo yǐn'),
                ],
            },
            {
                english: 'I plan to go to the park.',
                attempts: [
                    charGrades('我 wǒ ggg | 打 dǎ ggg | 算 suàn ggo | 去 qù ggg | 公 gōng ggg | 园 yuán gob yuǎn'),
                ],
            },
        ],
    },
    {
        id: '202-choose-next',
        kind: 'chooseNext',
        prompt: 'Nice work! Where would you like to go next?',
        options: [
            { emoji: '🧭', destination: { courseId: 2, lessonId: 203, step: 1 }, title: 'Lesson 6: Asking for Directions', detail: 'Next lesson in Chinese 2', response: 'Great — let’s learn how to find our way around.' },
            { emoji: '🔁', destination: { courseId: 2, lessonId: 202, step: 21, restart: true }, title: 'Practice this lesson again', detail: 'Replay the weekend dialogue', response: 'Good idea — practice makes it stick.' },
            { emoji: '🎯', destination: { courseId: 3, lessonId: 303, step: 1 }, title: 'Tone Pairs Challenge', detail: 'Pronunciation Lab', response: 'Let’s sharpen those tones!' },
        ],
    },
];

// ---------------------------------------------------------------------------
// Generic fallback pool (greetings/basics — fits any lesson), cycled to fill
// every position that has no authored content.
// ---------------------------------------------------------------------------

const genericPool: LessonExercise[] = [
    {
        id: 'pool-note-start',
        kind: 'image',
        caption: 'We will learn a few words first, then use them in a conversation.',
        chinese: { hanzi: '我们开始上课吧！', pinyin: 'Wǒmen kāishǐ shàng kè ba!' },
    },
    {
        id: 'pool-word-nihao',
        kind: 'word',
        hanzi: '你好',
        pinyin: 'nǐ hǎo',
        meaning: 'hello',
        emoji: '😊',
        infoText: 'Literally “you good” — the everyday greeting.',
        example: { hanzi: '你好，我是小美。', pinyin: 'Nǐ hǎo, wǒ shì Xiǎoměi.', english: "Hello, I'm Xiaomei." },
    },
    {
        id: 'pool-quiz-thanks',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'thank you'?",
        options: [
            { hanzi: '再见', pinyin: 'zàijiàn' },
            { hanzi: '谢谢', pinyin: 'xièxie' },
            { hanzi: '你好', pinyin: 'nǐ hǎo' },
            { hanzi: '请', pinyin: 'qǐng' },
        ],
        correctIndex: 1,
    },
    {
        id: 'pool-convo-hello',
        kind: 'conversation',
        mode: 'listen',
        title: 'Saying hello',
        lines: [
            {
                speakerHanzi: '老师', speakerEnglish: 'Teacher', side: 'left',
                hanzi: '你好！你叫什么名字？', pinyin: 'Nǐ hǎo! Nǐ jiào shénme míngzi?',
                english: "Hello! What's your name?",
                blankHanzi: '你好！你叫什么__？',
                grades: charGrades('你 nǐ ggg | 好 hǎo ggg | 你 nǐ ggg | 叫 jiào ggg | 什 shén ggg | 么 me ggg | 名 míng ggg | 字 zi ggg'),
            },
            {
                speakerHanzi: '学生', speakerEnglish: 'Student', side: 'right',
                hanzi: '你好，我叫大伟。', pinyin: 'Nǐ hǎo, wǒ jiào Dàwěi.',
                english: 'Hello, my name is Dawei.',
                blankHanzi: '你好，我叫__。',
                grades: charGrades('你 nǐ ggg | 好 hǎo ggg | 我 wǒ ggg | 叫 jiào ggo | 大 dà ggg | 伟 wěi gob wèi'),
            },
        ],
    },
    {
        id: 'pool-match-greetings',
        kind: 'match',
        prompt: 'Listen to each phrase and match it to its meaning.',
        leftMatch: 'secondAudio',
        rightMatch: 'english',
        pairs: [
            { hanzi: '你好', pinyin: 'nǐ hǎo', english: 'hello', emoji: '😊', second: { hanzi: '你好！', pinyin: 'Nǐ hǎo!' } },
            { hanzi: '谢谢', pinyin: 'xièxie', english: 'thank you', emoji: '🙏', second: { hanzi: '谢谢你！', pinyin: 'Xièxie nǐ!' } },
            { hanzi: '再见', pinyin: 'zàijiàn', english: 'goodbye', emoji: '👋', second: { hanzi: '明天见，再见！', pinyin: 'Míngtiān jiàn, zàijiàn!' } },
        ],
    },
    {
        id: 'pool-word-zaijian',
        kind: 'word',
        hanzi: '再见',
        pinyin: 'zàijiàn',
        meaning: 'goodbye',
        emoji: '👋',
        example: { hanzi: '明天见，再见！', pinyin: 'Míngtiān jiàn, zàijiàn!', english: 'See you tomorrow — bye!' },
    },
    {
        id: 'pool-listen-nihao',
        kind: 'listenRepeat',
        graded: true,
        target: {
            hanzi: '你好，很高兴认识你。',
            pinyin: 'Nǐ hǎo, hěn gāoxìng rènshi nǐ.',
            english: 'Hello, nice to meet you.',
            grades: charGrades('你 nǐ ggg | 好 hǎo ggo | 很 hěn ggg | 高 gāo ggg | 兴 xìng ggb xīng | 认 rèn ggg | 识 shi gog | 你 nǐ ggg'),
        },
        infoText: '很高兴认识你 is what you say when you meet someone for the first time.',
    },
    {
        id: 'pool-quiz-audio-tones',
        kind: 'quiz',
        mode: 'audio',
        prompt: 'Which recording says 妈 (mā, mom) with the first tone?',
        options: [
            { hanzi: 'má', pinyin: 'second tone', displayWord: '妈' },
            { hanzi: 'mā', pinyin: 'first tone', displayWord: '妈' },
            { hanzi: 'mà', pinyin: 'fourth tone', displayWord: '妈' },
            { hanzi: 'mǎ', pinyin: 'third tone', displayWord: '妈' },
        ],
        correctIndex: 1,
    },
    {
        id: 'pool-speak-name',
        kind: 'speak',
        graded: true,
        heroHanzi: '你叫什么名字？',
        heroPinyin: 'Nǐ jiào shénme míngzi?',
        speaker: { nameHanzi: '老师', nameEnglish: 'Teacher' },
        question: { hanzi: '你好！你叫什么名字？', pinyin: 'Nǐ hǎo! Nǐ jiào shénme míngzi?', english: "Hello! What's your name?" },
        studentCue: '我叫 ___ 。',
        accepted: [
            { hanzi: '我叫大伟。', pinyin: 'Wǒ jiào Dàwěi.', grades: charGrades('我 wǒ ggg | 叫 jiào ggo | 大 dà ggg | 伟 wěi gob wèi') },
        ],
        display: { starters: true },
        starters: ['我叫……', '我的名字是……'],
    },
];

// Supplement the opening practice with the remaining matching-column formats.
// Authored Restaurant / Weekend activities keep their existing step numbers.
const greetingMatch = genericPool.find((exercise): exercise is MatchGameExercise => exercise.kind === 'match')!;
genericPool.push(
    { ...greetingMatch, id: 'pool-match-pinyin-phrase', prompt: 'Match each word in pinyin to the phrase that uses it.', leftMatch: 'firstPinyin', rightMatch: 'secondCharacter' },
    { ...greetingMatch, id: 'pool-match-phrase-pinyin', prompt: 'Match each phrase in pinyin to its meaning.', leftMatch: 'secondPinyin', rightMatch: 'english' },
);

// Always returns exactly lesson.totalPrompts items, so completedPrompts doubles
// as the resume index. Authored content fills the lesson's final positions;
// everything else cycles the generic pool (ids re-keyed per position).
const authoredByLesson: Record<number, LessonExercise[]> = {
    201: orderingAtRestaurantExercises,
    202: weekendPlansExercises,
    401: [
        {
            id: 'open-text-audio', kind: 'openTextAudio', title: 'Write and speak',
            question: '你旅行的时候喜欢住什么样的地方？为什么？',
            guidance: 'Write your answer in Chinese, then record it. You can say the same ideas in your own words instead of reading your text exactly.',
            criteria: [
                { text: 'Describe the place you would choose', role: 'required' },
                { text: 'Give at least one reason', role: 'required' },
                { text: 'Add details that help a listener picture it', role: 'suggested' },
            ],
            speakingHint: 'Aim for about 45–90 seconds.',
        },
        {
            id: 'open-audio', kind: 'openAudio', title: 'Speak your answer',
            question: '说一说你最近做过的一个决定。你为什么这样选择？',
            guidance: 'Answer in your own words. You do not need to write a script.',
            criteria: [
                { text: 'Explain the decision', role: 'required' },
                { text: 'Give a reason', role: 'required' },
                { text: 'Say what happened or what you expect to happen', role: 'suggested' },
            ],
            speakingHint: 'Aim for about one minute.',
        },
        {
            id: 'open-text', kind: 'openText', title: 'Write your answer',
            question: '你觉得学习一门新语言时，什么方法最有帮助？为什么？',
            guidance: 'Write a short answer in Chinese. There are many good ways to respond.',
            criteria: [
                { text: 'Name a method', role: 'required' },
                { text: 'Explain why it helps', role: 'required' },
                { text: 'You could describe a time you used it', role: 'suggested' },
            ],
        },
    ],
};

const revisionFeedbackByKind: Record<OpenResponseExercise['kind'], [string, ...string[]]> = {
    openTextAudio: [
        'Add one specific feature of the place and explain how it supports your reason. Include that detail in both your writing and new recording.',
        'Make the connection between your choice and your reason easy to follow in both versions. Your spoken wording can differ from your writing.',
        'Give your answer a clear opening, a reason, and a closing thought before you record again.',
    ],
    openAudio: [
        'Add what happened after your decision, or what you expect to happen. Connect that outcome to your reason in a new recording.',
        'Try putting the decision, your reason, and the result in a clear order for your listener.',
        'End with one sentence about how you feel about the decision now.',
    ],
    openText: [
        'Add one short example of when you used this method and explain how it helped you learn.',
        'Connect your example back to the method so the reader can see why it helped.',
        'Try ending with a sentence that answers the question directly.',
    ],
};

// The second lesson reuses the same questions to compare a final-submission flow
// with a feedback-and-revision flow. Its feedback is scripted preview content.
authoredByLesson[402] = authoredByLesson[401].map(exercise => {
    if (exercise.kind !== 'openTextAudio' && exercise.kind !== 'openAudio' && exercise.kind !== 'openText') {
        throw new Error('The feedback preview requires an open-response exercise');
    }
    return { ...exercise, id: `${exercise.id}-feedback`, revisionFeedback: revisionFeedbackByKind[exercise.kind] };
});

// Lesson 403 keeps lesson 402's feedback-and-revision flow but shows structured
// feedback on a sample answer: annotated sentences, numbered language items and,
// for a recording, per-character initial / final / tone grades with sound
// patterns and pronunciation tips (as the open-response pilot produces them).
const feedbackPreviewByKind: Record<OpenResponseExercise['kind'], OpenResponseFeedbackPreview> = {
    openTextAudio: {
        source: 'script',
        focus: ['Count hotels with 家: 一家酒店', 'Fourth tone on 店: diàn'],
        sentences: [
            {
                text: '我旅行的时候喜欢住酒店。', clip: '0:00',
                grades: charGrades('我 wǒ ggg|旅 lǚ ggg|行 xíng ggo|的 de ggg|时 shí ggg|候 hou ggg|喜 xǐ ggg|欢 huan ggg|住 zhù bgg jù|酒 jiǔ ggg|店 diàn ggo diān'),
            },
            {
                text: '酒店很方便，服务也很好。', clip: '0:04',
                grades: charGrades('酒 jiǔ ggg|店 diàn ggb diān|很 hěn ggg|方 fāng ggo fàng|便 biàn ggo|服 fú ggg|务 wù ggo|也 yě ggg -|很 hěn ggg|好 hǎo ggg'),
            },
            {
                text: '我一般住在市中心，因为去哪里都很近。', clip: '0:09',
                // The recording said 哪儿 where the writing has 哪里; those sounds are graded as spoken.
                grades: charGrades('我 wǒ ggg|一 yì ggg|般 bān ggg|住 zhù ogg jù|在 zài ggg|市 shì ggo|中 zhōng ggg|心 xīn ggg|因 yīn ggg|为 wèi ggg|去 qù ggg|哪 nǎ ggg|儿 r ggg|都 dōu ggg|很 hěn ggg|近 jìn ggo'),
                heardDifferently: 'You said 哪儿 where you wrote 哪里. That is fine: both are correct, and those sounds are graded as you said them.',
            },
            {
                text: '上次我去上海，我住了一个很干净的酒店。', clip: '0:15',
                grades: charGrades('上 shàng ggg|次 cì ggo|我 wǒ ggg|去 qù ggg|上 shàng ggg|海 hǎi ggg|我 wǒ ggg|住 zhù ggg|了 le ggg|一 yí ggg|个 gè ggg|很 hěn ggg|干 gān gbg gāng|净 jìng ggo|的 de ggg|酒 jiǔ ggg|店 diàn ggo diān'),
            },
        ],
        language: [
            {
                tier: 'correction', span: '一个很干净的酒店', revised: '上次我去上海，我住了一家很干净的酒店。',
                explanation: 'Hotels, restaurants and shops are counted with 家, so say 一家酒店. With 个 the sentence is understandable but sounds unnatural.',
                exampleZh: '这条街上有三家咖啡馆。', exampleEn: 'There are three cafés on this street.',
            },
            {
                tier: 'correction', span: '去哪里都很近', revised: '我一般住在市中心，因为离哪里都很近。',
                explanation: 'Distance uses 离 + place + 近 or 远. 去 is about going somewhere, so 去哪里都很近 does not quite say that everything is close.',
                exampleZh: '我家离地铁站很近。', exampleEn: 'My home is close to the subway station.',
            },
            {
                tier: 'suggestion', span: '酒店很方便', revised: '住酒店很方便，而且服务也很好。',
                explanation: 'Say what is convenient: staying in a hotel (住酒店). 而且 links your second reason more clearly.',
                exampleZh: '坐高铁很快，而且很舒服。', exampleEn: 'Taking the high-speed train is fast, and comfortable too.',
            },
        ],
        patterns: [
            { label: 'Level and falling tones mixed up (1st ↔ 4th)', count: 4, examples: ['酒店 jiǔdiàn', '方便 fāngbiàn'] },
            { label: 'zh said like j', count: 2, examples: ['住 zhù'] },
        ],
        tips: [
            { word: '酒店', expected: 'jiǔ diàn', heard: 'jiǔ diān', strength: 'likely', clip: '0:04', tip: 'The recording suggests 店 stayed high and level. Start high and drop sharply for the fourth tone: diàn.' },
            { word: '住', expected: 'zhù', heard: 'jù', strength: 'likely', clip: '0:00', tip: 'The recording suggests 住 started like j. Curl your tongue tip back for the retroflex zh, then let the tone fall: zhù.' },
            { word: '干净', expected: 'gān jìng', heard: 'gāng jìng', strength: 'possible', clip: '0:15', tip: '干 may have ended in -ng. Finish it with your tongue tip behind your top teeth (-n) before jìng.' },
        ],
    },
    openAudio: {
        source: 'transcript',
        focus: ['Drop 了 after 很远 for a lasting fact', 'Dipping tone on 买 (mǎi), or it sounds like 卖'],
        sentences: [
            {
                text: '上个月我决定搬家。', clip: '0:00',
                grades: charGrades('上 shàng ggg|个 ge ggg|月 yuè ggo|我 wǒ ggg|决 jué ggo|定 dìng ggg|搬 bān gog bāng|家 jiā ggg'),
            },
            {
                text: '我以前住的公寓离公司很远了。', clip: '0:03',
                grades: charGrades('我 wǒ ggg|以 yǐ ggg|前 qián ggg|住 zhù ggg|的 de ggg|公 gōng ggg|寓 yù ggg|离 lí ggg|公 gōng ggg|司 sī ggg|很 hěn ggg|远 yuǎn ggb yuàn|了 le ggg'),
            },
            {
                text: '每天坐地铁要一个半小时。', clip: '0:08',
                grades: charGrades('每 měi ggg|天 tiān ggo|坐 zuò ggo|地 dì ggg|铁 tiě ggg|要 yào ggg|一 yí ggg|个 ge ggg|半 bàn gbg bàng|小 xiǎo ggg|时 shí ggg'),
            },
            {
                text: '搬家以后，我买了一张新的床。', clip: '0:12',
                grades: charGrades('搬 bān gog bāng|家 jiā ggg|以 yǐ ggg|后 hòu ggo|我 wǒ ggg|买 mǎi ggb mài|了 le ggg|一 yì ggg|张 zhāng ogo|新 xīn ggg|的 de ggg|床 chuáng ogg'),
                heardDifferently: '买 was heard as mài, which is 卖 “sell”. If you meant “buy”, see the tone tip below.',
            },
            { text: '现在我每天睡得更好。', clip: '0:17', unclear: true },
            {
                text: '我觉得这个决定很对。', clip: '0:21',
                grades: charGrades('我 wǒ ggg|觉 jué ggo|得 de ggg|这 zhè ggg|个 ge ggg|决 jué ggg|定 dìng ggg|很 hěn ggg|对 duì ggo'),
            },
        ],
        language: [
            {
                tier: 'correction', span: '离公司很远了', revised: '我以前住的公寓离公司很远。',
                explanation: '了 after 很 + an adjective announces a change: it has become far. For something that was simply true, drop 了, or say 太远了 if you are complaining.',
                exampleZh: '这家饭馆的菜很贵。', exampleEn: 'The food at this restaurant is expensive.',
            },
            {
                tier: 'correction', span: '这个决定很对', revised: '我觉得这个决定是对的。',
                explanation: 'To judge a choice as right, say 是对的. 很对 on its own sounds like agreeing with something someone said.',
                exampleZh: '我觉得你的选择是对的。', exampleEn: 'I think your choice was right.',
            },
            {
                tier: 'suggestion', span: '每天坐地铁要一个半小时', revised: '每天坐地铁上班要一个半小时。',
                explanation: 'Adding 上班 makes clear that the trip was your commute, which is the reason for your decision.',
                exampleZh: '我每天骑车上学要二十分钟。', exampleEn: 'Riding my bike to school takes twenty minutes every day.',
            },
        ],
        patterns: [
            { label: '-an said like -ang', count: 3, examples: ['搬家 bānjiā', '一个半 yí ge bàn'] },
            { label: 'Dipping (3rd) tone said as falling (4th)', count: 2, examples: ['买 mǎi', '远 yuǎn'] },
        ],
        tips: [
            { word: '买', expected: 'mǎi', heard: 'mài', strength: 'likely', clip: '0:12', tip: 'The recording suggests 买 fell like a fourth tone, which turns it into 卖 “sell”. Keep it low and dipping: mǎi.' },
            { word: '远', expected: 'yuǎn', heard: 'yuàn', strength: 'likely', clip: '0:03', tip: 'The recording suggests 远 fell sharply. Keep it low; before 了 it can stay low without rising at the end.' },
            { word: '搬家', expected: 'bān jiā', heard: 'bāng jiā', strength: 'possible', clip: '0:00', tip: '搬 may have ended in -ng. Close it with your tongue tip behind your top teeth (-n): bān.' },
        ],
    },
    openText: {
        source: 'writing',
        focus: ['Say what is too fast: 说得太快', 'Add what you say after 说: 说中文'],
        sentences: [
            { text: '我觉得每天说是最有帮助的方法。' },
            { text: '我常常跟中国朋友一起练习说话。' },
            { text: '他们帮我改错误，所以我的发音越来越好了。' },
            { text: '我也看中国电影，这个方法很有意思但是有时候太快。' },
        ],
        language: [
            {
                tier: 'correction', span: '这个方法很有意思但是有时候太快', revised: '我也看中国电影，这个方法很有意思，但是电影里的人有时候说得太快。',
                explanation: 'Say what is too fast: the people in the film speaking (说得太快), not the method. A comma before 但是 also makes the contrast easier to read.',
                exampleZh: '这本书很有意思，但是有些字太难了。', exampleEn: 'This book is interesting, but some characters are too hard.',
            },
            {
                tier: 'correction', span: '每天说是最有帮助的方法', revised: '我觉得每天说中文是最有帮助的方法。',
                explanation: 'When speaking is the topic of the sentence, say what you speak: 每天说中文. Without it, 每天说 sounds unfinished.',
                exampleZh: '每天读中文新闻对我很有帮助。', exampleEn: 'Reading Chinese news every day helps me a lot.',
            },
            {
                tier: 'suggestion', span: '改错误', revised: '他们帮我改错，所以我的发音越来越好了。',
                explanation: '改错 (or the more formal 改正错误) is the usual way to say “correct mistakes”. 改错误 is understandable but less common.',
                exampleZh: '老师帮我们改作业。', exampleEn: 'The teacher corrects our homework.',
            },
        ],
    },
};

authoredByLesson[403] = authoredByLesson[401].map(exercise => {
    if (exercise.kind !== 'openTextAudio' && exercise.kind !== 'openAudio' && exercise.kind !== 'openText') {
        throw new Error('The detailed feedback preview requires an open-response exercise');
    }
    return {
        ...exercise, id: `${exercise.id}-detailed`, revisionFeedback: revisionFeedbackByKind[exercise.kind],
        feedbackPreview: feedbackPreviewByKind[exercise.kind],
    };
});

export function getLessonExercises(lesson: StudentLesson): LessonExercise[] {
    const authored = authoredByLesson[lesson.id] ?? [];
    const authoredStart = Math.max(0, lesson.totalPrompts - authored.length);
    const exercises: LessonExercise[] = [];
    for (let i = 0; i < lesson.totalPrompts; i++) {
        if (i >= authoredStart && i - authoredStart < authored.length) {
            exercises.push(authored[i - authoredStart]);
        } else {
            const pooled = genericPool[i % genericPool.length];
            exercises.push({ ...pooled, id: `${lesson.id}-${i}-${pooled.id}` });
        }
    }
    return exercises;
}

// ---------------------------------------------------------------------------
// Practice sessions ("sets") — a lesson is split into consecutive sessions. When a
// session ends, the player offers the next one (the v2 home of the legacy
// "Start Next Lesson" prompt); the last session ends the lesson.
// ---------------------------------------------------------------------------

export interface LessonSession {
    number: number;
    start: number;
    end: number; // exclusive
}

export function getLessonSessions(lesson: StudentLesson): LessonSession[] {
    const total = lesson.totalPrompts;
    const count = total >= 24 ? 3 : total >= 12 ? 2 : 1;
    const size = Math.ceil(total / count);
    return Array.from({ length: count }, (_, index) => ({
        number: index + 1,
        start: index * size,
        end: Math.min(total, (index + 1) * size),
    })).filter(session => session.start < session.end);
}

export function sessionForStep(lesson: StudentLesson, step: number): LessonSession {
    const sessions = getLessonSessions(lesson);
    return sessions.find(session => step >= session.start && step < session.end) ?? sessions[sessions.length - 1];
}

// ---------------------------------------------------------------------------
// Speaking feedback helpers
// ---------------------------------------------------------------------------

const gradePoints: Record<SoundGrade, number> = { good: 100, ok: 65, bad: 30 };

export function speakingScore(grades: CharacterGrade[]): number {
    if (grades.length === 0) {
        return 0;
    }
    const total = grades.reduce((sum, g) => sum + gradePoints[g.initial] + gradePoints[g.final] + gradePoints[g.tone], 0);
    return Math.round(total / (grades.length * 3));
}

// Generic fallback when the teacher authored no response.
export function scoreMessage(outcome: ScoreOutcome): { title: string; subtitle: string } {
    if (outcome === 'good') {
        return { title: 'Great job!', subtitle: 'That passes — check the details to polish a few sounds.' };
    }
    if (outcome === 'medium') {
        return { title: 'You’re on the right track!', subtitle: 'Would you like to try again?' };
    }
    return { title: 'Needs practice', subtitle: 'Take your time, then give it another try.' };
}

function shiftGrade(grade: SoundGrade, direction: 1 | -1): SoundGrade {
    const order: SoundGrade[] = ['bad', 'ok', 'good'];
    return order[Math.max(0, Math.min(2, order.indexOf(grade) + direction))];
}

// Score band each scripted outcome should land in, so the ring agrees with the
// message (pass ≥ 85, medium 60–78, fail ≤ 55).
const outcomeBands: Record<ScoreOutcome, { min: number; max: number }> = {
    good: { min: 85, max: 100 },
    medium: { min: 60, max: 78 },
    bad: { min: 0, max: 55 },
};

// Grades for a scripted take: starting from the authored grades, characters are
// nudged one sound at a time (tone first, then final, then initial) until the
// score falls inside the outcome's band. Deterministic, so demos replay the same.
export function gradesForOutcome(grades: CharacterGrade[], outcome: ScoreOutcome): CharacterGrade[] {
    if (grades.length === 0) {
        return grades;
    }
    const band = outcomeBands[outcome];
    const result = grades.map(grade => ({ ...grade }));
    const parts: ('tone' | 'final' | 'initial')[] = ['tone', 'final', 'initial'];
    for (let step = 0; step < grades.length * 6; step++) {
        const score = speakingScore(result);
        if (score >= band.min && score <= band.max) {
            break;
        }
        const direction = score < band.min ? 1 : -1;
        const grade = result[step % result.length];
        const part = parts[Math.floor(step / result.length) % parts.length];
        grade[part] = shiftGrade(grade[part], direction);
    }
    for (const grade of result) {
        if (grade.initial === 'good' && grade.final === 'good' && grade.tone === 'good') {
            grade.pinyinSpoken = grade.pinyinExpected;
        } else if (grade.tone !== 'good' && grade.pinyinSpoken === grade.pinyinExpected) {
            // A missed tone is heard as the toneless syllable.
            grade.pinyinSpoken = grade.pinyinExpected.normalize('NFD').replace(/[̀́̄̌]/g, '').normalize('NFC');
        }
    }
    return result;
}

// Mock stand-ins for the server checks behind typed answers.
const normalize = (text: string) => text.replace(/[\s，。！？、,.!?]/g, '');

export function matchingAccepted<T extends Sentence>(answer: string, accepted: T[]): T | undefined {
    return accepted.find(item => normalize(item.hanzi) === normalize(answer));
}

// Closest accepted answer by shared characters — the legacy model shown to speak.
export function closestAccepted<T extends Sentence>(answer: string, accepted: T[]): T {
    const typed = new Set(normalize(answer));
    let best = accepted[0];
    let bestScore = -1;
    for (const item of accepted) {
        const chars = normalize(item.hanzi);
        const overlap = chars.split('').filter(char => typed.has(char)).length / Math.max(chars.length, 1);
        if (overlap > bestScore) {
            best = item;
            bestScore = overlap;
        }
    }
    return best;
}

// Pinyin for the characters this mock lesson uses, so a V2 student-authored
// sentence can show per-character feedback. Unknown characters show no pinyin.
const mockPinyin: Record<string, string> = {
    我: 'wǒ', 你: 'nǐ', 他: 'tā', 她: 'tā', 喜: 'xǐ', 欢: 'huan', 吃: 'chī', 喝: 'hē', 饺: 'jiǎo', 子: 'zi',
    米: 'mǐ', 饭: 'fàn', 面: 'miàn', 条: 'tiáo', 茶: 'chá', 水: 'shuǐ', 咖: 'kā', 啡: 'fēi', 很: 'hěn',
    也: 'yě', 和: 'hé', 包: 'bāo', 鱼: 'yú', 肉: 'ròu', 菜: 'cài', 一: 'yì', 杯: 'bēi', 碗: 'wǎn',
    盘: 'pán', 请: 'qǐng', 给: 'gěi', 要: 'yào', 不: 'bù', 都: 'dōu', 最: 'zuì', 中: 'zhōng', 国: 'guó',
    的: 'de', 是: 'shì', 好: 'hǎo', 汤: 'tāng', 果: 'guǒ', 苹: 'píng', 蛋: 'dàn', 鸡: 'jī',
};

export function gradesForText(text: string): CharacterGrade[] {
    return normalize(text).split('').map((character, index, all) => {
        const pinyin = mockPinyin[character] ?? '';
        const last = index === all.length - 1;
        return { character, pinyinExpected: pinyin, pinyinSpoken: pinyin, initial: 'good', final: 'good', tone: last ? 'ok' : 'good' };
    });
}

export function blankOut(text: string): string {
    return text.replace(/[^\s，。！？、,.!?]/g, '_');
}
